import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactECharts from 'echarts-for-react';
import { subDays } from 'date-fns';
import { analyticsService } from '../../../services/analyticsService';
import { Spinner } from '../../../components/ui/Spinner';
import { Button } from '../../../components/ui/Button';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

export const Analytics = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [cardStats, setCardStats] = useState([]);
  const [intelligence, setIntelligence] = useState(null);
  const [period, setPeriod] = useState('30');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const end = new Date();
        const start = period === 'all' ? new Date(0) : subDays(end, parseInt(period, 10));
        
        const [dashboardData, performanceData, intelligenceData] = await Promise.all([
          analyticsService.getDashboardStats(start, end),
          analyticsService.getCardsPerformance(start, end),
          analyticsService.getIntelligenceSummary(start, end)
        ]);
        
        setStats(dashboardData);
        setCardStats((performanceData || []).sort((a,b) => b.card_opens - a.card_opens));
        setIntelligence(intelligenceData);
      } catch (err) {
        console.error('Failed to load analytics:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [period]);

  const insights = useMemo(() => {
    if (!stats || stats.summary.card_opens === 0) return [];
    
    const items = [];
    const { qr_opens, direct_opens, card_opens, connections } = stats.summary;
    
    if (qr_opens > direct_opens) {
      items.push("Most of your card visits came from QR codes.");
    } else if (direct_opens > qr_opens) {
      items.push("Direct links generated more card opens than QR codes.");
    }
    
    const connRate = Math.round((connections / card_opens) * 100);
    if (connRate > 10) {
      items.push(`Your connection conversion rate is strong at ${connRate}%.`);
    }
    
    if (cardStats.length > 1) {
      const topConn = [...cardStats].sort((a,b) => b.connections - a.connections)[0];
      if (topConn && topConn.connections > 0) {
        items.push(`Your "${topConn.internal_name}" card generated the most new connections.`);
      }
    }
    
    return items;
  }, [stats, cardStats]);

  const chartOptions = useMemo(() => {
    if (!stats || !stats.trends || stats.trends.length === 0) return null;
    
    return {
      tooltip: { trigger: 'axis' },
      legend: { data: ['Opens', 'QR Scans', 'Saves', 'Connections'], bottom: 0 },
      grid: { left: '3%', right: '4%', bottom: '15%', top: '5%', containLabel: true },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: stats.trends.map(t => new Date(t.day).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }))
      },
      yAxis: { type: 'value', minInterval: 1 },
      series: [
        { name: 'Opens', type: 'line', data: stats.trends.map(t => t.opens), smooth: true, itemStyle: { color: '#3b82f6' } },
        { name: 'QR Scans', type: 'line', data: stats.trends.map(t => t.qr_scans), smooth: true, itemStyle: { color: '#a855f7' } },
        { name: 'Saves', type: 'line', data: stats.trends.map(t => t.saves), smooth: true, itemStyle: { color: '#10b981' } },
        { name: 'Connections', type: 'line', data: stats.trends.map(t => t.connections), smooth: true, itemStyle: { color: '#f59e0b' } },
      ]
    };
  }, [stats]);

  if (loading) {
    return <div className="flex justify-center py-20"><Spinner size="lg" /></div>;
  }

  const hasData = stats && stats.summary && stats.summary.card_opens > 0;
  const summary = stats?.summary || {};
  const saveRate = summary.card_opens > 0 ? Math.round((summary.contact_saves / summary.card_opens) * 100) : 0;
  const connRate = summary.card_opens > 0 ? Math.round((summary.connections / summary.card_opens) * 100) : 0;
  
  const dataQuality = intelligence?.data_quality?.attributed_interactions_percentage || 0;
  const readinessPercentage = Math.round(dataQuality); // Fixed calculation

  return (
    <div className="max-w-6xl pb-12">
      <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Analytics & Insights</h1>
          <p className="mt-2 text-slate-600">Track how your network is growing over time.</p>
        </div>
        <select 
          className="rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-medium focus:ring-2 focus:ring-slate-900 outline-none bg-white"
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
        >
          <option value="7">Last 7 Days</option>
          <option value="30">Last 30 Days</option>
          <option value="90">Last 90 Days</option>
          <option value="all">All Time</option>
        </select>
      </header>

      {/* Intelligence Readiness Panel */}
      <div className="bg-slate-900 rounded-2xl p-4 sm:p-6 mb-8 flex flex-col sm:flex-row items-center justify-between gap-4 shadow-sm text-white">
        <div className="flex items-center gap-4 w-full sm:w-auto">
          <div className="w-12 h-12 rounded-full bg-slate-800 flex items-center justify-center shrink-0 border border-slate-700">
            <SafeIcon icon={FiIcons.FiCpu} className="text-2xl text-blue-400" />
          </div>
          <div>
            <h3 className="font-bold text-base">Meetoi Intelligence Readiness: {readinessPercentage}%</h3>
            <p className="text-sm text-slate-400">Data foundation for AI insights gathering.</p>
          </div>
        </div>
        
        <div className="flex gap-4 text-xs text-slate-300 w-full sm:w-auto flex-wrap sm:flex-nowrap">
          <div className="bg-slate-800 px-3 py-2 rounded-lg border border-slate-700">
            <span className="block font-bold text-white mb-0.5">{intelligence?.activity?.unique_visitors || 0}</span>
            Unique Visitors
          </div>
          <div className="bg-slate-800 px-3 py-2 rounded-lg border border-slate-700">
            <span className="block font-bold text-white mb-0.5">{intelligence?.activity?.outcomes_recorded || 0}</span>
            Outcomes Recorded
          </div>
        </div>
      </div>

      {!hasData ? (
        <div className="bg-white rounded-2xl border border-slate-200 border-dashed p-12 text-center">
          <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <SafeIcon icon={FiIcons.FiBarChart2} className="text-2xl text-slate-400" />
          </div>
          <h3 className="text-lg font-bold text-slate-900 mb-2">No activity yet</h3>
          <p className="text-slate-500 max-w-sm mx-auto mb-6">
            Share your card or QR code to start collecting insights and tracking your performance.
          </p>
          <Button onClick={() => navigate('/app/cards')}>Manage Cards</Button>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Top Metrics */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            <MetricCard label="Public Card Opens" value={summary.card_opens} icon={FiIcons.FiEye} color="blue" />
            <MetricCard label="QR Scans" value={summary.qr_scans} icon={FiIcons.FiMaximize} color="purple" />
            <MetricCard label="Contact Saves" value={summary.contact_saves} icon={FiIcons.FiDownload} color="emerald" />
            <MetricCard label="New Connections" value={summary.connections} icon={FiIcons.FiUsers} color="amber" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Chart */}
            <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
              <h3 className="text-base font-bold text-slate-900 mb-6">Performance Trends</h3>
              <div className="h-80 w-full">
                {chartOptions ? (
                  <ReactECharts option={chartOptions} style={{ height: '100%', width: '100%' }} />
                ) : (
                  <div className="h-full flex items-center justify-center text-slate-400">Not enough data to graph</div>
                )}
              </div>
            </div>

            {/* Insights and Conversions */}
            <div className="space-y-8">
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
                <h3 className="text-base font-bold text-slate-900 mb-4">Conversion Rates</h3>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-medium text-slate-600">Save Rate</span>
                      <span className="font-bold text-slate-900">{saveRate}%</span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-2">
                      <div className="bg-emerald-500 h-2 rounded-full" style={{ width: `${Math.min(saveRate, 100)}%` }}></div>
                    </div>
                    <p className="text-xs text-slate-400 mt-1">Saves per card open</p>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-medium text-slate-600">Connection Rate</span>
                      <span className="font-bold text-slate-900">{connRate}%</span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-2">
                      <div className="bg-amber-500 h-2 rounded-full" style={{ width: `${Math.min(connRate, 100)}%` }}></div>
                    </div>
                    <p className="text-xs text-slate-400 mt-1">Submissions per card open</p>
                  </div>
                </div>
              </div>

              {insights.length > 0 && (
                <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl shadow-sm p-6 text-white">
                  <h3 className="text-base font-bold mb-4 flex items-center">
                    <SafeIcon icon={FiIcons.FiZap} className="mr-2 text-yellow-400" /> Key Insights
                  </h3>
                  <ul className="space-y-3">
                    {insights.map((insight, idx) => (
                      <li key={idx} className="text-sm text-slate-200 flex items-start">
                        <span className="text-slate-400 mr-2 mt-0.5">•</span>
                        {insight}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* Cards Breakdown */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
            <div className="px-6 py-5 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
              <h3 className="font-bold text-slate-900">Card Performance</h3>
            </div>
            
            {cardStats.length === 0 ? (
              <div className="p-8 text-center text-slate-500">No card activity found in this period.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-white text-xs font-bold text-slate-500 uppercase tracking-wider border-b border-slate-100">
                      <th className="px-6 py-4">Card Name</th>
                      <th className="px-6 py-4 text-right">Opens</th>
                      <th className="px-6 py-4 text-right">QR Scans</th>
                      <th className="px-6 py-4 text-right">Saves</th>
                      <th className="px-6 py-4 text-right">Connections</th>
                      <th className="px-6 py-4"></th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-slate-50">
                    {cardStats.map(card => (
                      <tr key={card.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-4 font-medium text-slate-900">{card.internal_name}</td>
                        <td className="px-6 py-4 text-right text-slate-600">{card.card_opens}</td>
                        <td className="px-6 py-4 text-right text-slate-600">{card.qr_scans}</td>
                        <td className="px-6 py-4 text-right text-slate-600">{card.contact_saves}</td>
                        <td className="px-6 py-4 text-right text-slate-600">{card.connections}</td>
                        <td className="px-6 py-4 text-right">
                          <Button variant="ghost" size="sm" onClick={() => navigate(`/app/cards/${card.id}/analytics`)}>
                            View Details
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const MetricCard = ({ label, value, icon, color }) => {
  const colorMap = {
    blue: 'text-blue-600 bg-blue-50',
    purple: 'text-purple-600 bg-purple-50',
    emerald: 'text-emerald-600 bg-emerald-50',
    amber: 'text-amber-600 bg-amber-50',
  };

  return (
    <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex flex-col items-center sm:items-start text-center sm:text-left">
      <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-4 ${colorMap[color]}`}>
        <SafeIcon icon={icon} className="text-xl" />
      </div>
      <p className="text-3xl font-bold text-slate-900 mb-1">{value || 0}</p>
      <p className="text-sm font-medium text-slate-500">{label}</p>
    </div>
  );
};